import React from 'react';
import { useTranslation } from 'react-i18next';
import { MyClientsOrderDetail } from '@1po/1po-bff-fe-spec/generated/my_clients_order/model/MyClientsOrderDetail';
import {
  HeaderRow,
  OrderHeaderBar,
} from 'pages/MyOrdersPage/ClientsOrders/ClientOrderDetail/ClientOrderDetailPage.styled';
import {
  getMyClientsOrderDeliveryMethod,
  getMyClientsOrderStatus,
} from 'pages/MyOrdersPage/ClientsOrders/ClientsOrders.utils';
import { Flex, Text } from 'UI';
import { textFormatter } from 'utils';
import { isValidDate } from 'utils/format/formattingService';

interface OrderHeaderProps {
  order: MyClientsOrderDetail;
}

const OrderHeader = ({ order }: OrderHeaderProps) => {
  const { t } = useTranslation();

  const formatDate = (dateStr?: string) => {
    if (!dateStr || !isValidDate(dateStr)) return '—';
    return textFormatter.formatTime(new Date(dateStr));
  };

  const renderLabel = (text: string) => (
    <Text type="base" bold color="grey35">
      {text}&nbsp;:
    </Text>
  );

  const renderValue = (text?: string) => (
    <Text type="base" bold>
      {text || '—'}
    </Text>
  );

  return (
    <OrderHeaderBar direction={'row'} gap={30} align={'flex-start'} justify={'space-between'} data-cy={'order-header'}>
      <HeaderRow>
        {renderLabel(t('clients_order.detail.bir_code', 'BIR Code'))}
        {renderValue(order.garageBirCode)}

        {renderLabel(t('clients_order.detail.tresor_code', 'Tresor Code'))}
        {renderValue(order.garageTresorCode)}

        {renderLabel(t('clients_order.order_number', 'Order n°'))}
        {renderValue(order.orderNumber)}

        {renderLabel(t('clients_order.rpartstore_order_number', 'Rpartstore n°'))}
        {renderValue(order.rPartStoreOrderNumber)}

        {renderLabel(t('clients_order.detail.acknowledged', 'Acknowledged'))}
        {renderValue(order.dmsAcknowledged ? t('common.yes', 'Yes') : t('common.no', 'No'))}

        {renderLabel(t('clients_order.detail.dms_number', 'DMS number'))}
        {renderValue(order.garageDmsNumber)}

        {renderLabel(t('order.order_detail.order_date', 'Date'))}
        {renderValue(formatDate(order.orderDate))}

        {renderLabel(t('order.order_detail.ordered_by', 'Ordered by'))}
        {renderValue(order.garageName)}

        {renderLabel(t('clients_order.detail.treated_by', 'Treated by'))}
        {renderValue(order.treatedBy)}
      </HeaderRow>
      <HeaderRow>
        {renderLabel(t('order.order_detail.status', 'Status'))}
        {getMyClientsOrderStatus(order.status, t)}

        {renderLabel(t('clients_order.detail.delivery', 'Delivery'))}
        {renderValue(getMyClientsOrderDeliveryMethod(order.deliveryMethod, t))}

        {renderLabel(t('order.order_detail.order_mark', 'Order Mark'))}
        {renderValue(order.mark)}

        {renderLabel(t('order.order_detail.message', 'Message'))}
        {renderValue(order.message)}

        {order.validatedOrRejectedBy && (
          <>
            {renderLabel(t('clients_order.detail.validated_by', 'Validated / Rejected by'))}
            <Flex />
            {renderValue(`[${order.validatedOrRejectedBy}]`)}
          </>
        )}
      </HeaderRow>
      <Flex direction={'column'} size={1} justify={'center'} align={'flex-end'} alignSelf={'center'} maxWidth={400}>
        <Flex direction={'row'} gap={10} align={'baseline'}>
          <Text type="base" bold color="grey35">
            {t('common.price.total_VAT_excluded', 'Total Excl.VTA')}
          </Text>
          <Text type="base" color="grey55">
            {textFormatter.formatCurrency(Number(order.totalTaxFree ?? 0), order.currency)}
          </Text>
          <Text type="base" bold color="grey35">
            {t('common.price.vat', 'VAT')}
          </Text>
          <Text type="base" color="grey55">
            {textFormatter.formatCurrency(Number(order.totalTax ?? 0), order.currency)}
          </Text>
        </Flex>
        <Flex direction={'row'} gap={20}>
          <Text type="heading4">{t('common.price.total_vat_incl', 'Total Incl.VAT')}</Text>
          <Text type="heading4">
            {textFormatter.formatCurrency(Number(order.totalTaxIncluded ?? 0), order.currency)}
          </Text>
        </Flex>
        {order.status === 'DELIVERED' && (
          <Flex direction={'row'} padding={'15px 0px'}>
            {getMyClientsOrderStatus(order.status, t)}
          </Flex>
        )}
      </Flex>
    </OrderHeaderBar>
  );
};

export default OrderHeader;
